/**
 * This file is the entry point for the React app, it sets up the root
 * element and renders the App component to the DOM.
 *
 * It is included in `src/index.html`.
 */

import { confirm } from "@devhop/ui";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createPersistOptions } from "./app/providers/query-persister";
import { router } from "./app/router";
import "./index.css";
import { ensurePersistentStorage } from "./shared/lib/persistent-storage";
import { registerSW } from "./shared/lib/register-sw";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 24 ชม. - ต้องยาวพอให้ persistQueryClient เก็บ snapshot ของ query สำคัญ
      // (เช่น กะ) ข้ามการ reboot ได้ ก่อนที่ garbage collector จะลบทิ้ง
      gcTime: 1000 * 60 * 60 * 24,
    },
  },
});

const persistOptions = createPersistOptions();

// Request durable storage ASAP so offline orders in IndexedDB are not evicted
// when the device restarts or runs low on storage.
void ensurePersistentStorage();

const elem = document.getElementById("root");
if (!elem) throw new Error("Root element not found");

const app = (
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={persistOptions}
    >
      <RouterProvider router={router} />
      {/* {process.env.NODE_ENV !== "production" ? (
        <ReactQueryDevtools buttonPosition="bottom-right" />
      ) : null} */}
    </PersistQueryClientProvider>
  </StrictMode>
);

function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;

  registerSW((activateUpdate) => {
    if (import.meta.hot) return;
    showUpdatePrompt(activateUpdate);
  });
}

async function showUpdatePrompt(activateUpdate: () => void) {
  const ok = await confirm({
    title: "ມີເວີຊັນໃໝ່ພ້ອມໃຫ້ໃຊ້ງານ",
    description: "ຕ້ອງການອັບເດດເປັນເວີຊັນຫຼ້າສຸດດຽວນີ້ບໍ?",
    actionText: "ອັບເດດ",
    ActionProps: {
      variant: "default",
    },
  });

  if (ok) {
    activateUpdate();
  }
}

if (import.meta.hot) {
  // With hot module reloading, `import.meta.hot.data` is persisted.
  let root = import.meta.hot.data.root;
  if (!root) {
    root = createRoot(elem);
    import.meta.hot.data.root = root;
  }
  root.render(app);
  registerServiceWorker();
} else {
  // The hot module reloading API is not available in production.
  createRoot(elem).render(app);
  registerServiceWorker();
}
